
import React, { useState, useEffect } from "react";
import { Modal } from "../../../components/Modal";
import { FileUploadKit } from "../../../components/common/FileUploadKit";
import { useSignRequestWorkflow } from "../useSignRequestWorkflow";
import type { SignRequest, SignSourceType } from "../../../types/signRequest";
import type { AttachmentMeta } from "../../../types/attachments";
import { uploadApprovalFile, createApprovalRequest, updateApprovalRequest } from "../../../services/approvalRequestsService"; 
import { createOrResetApprovalFlow } from "../../../services/approvalFlowService";
import { useAuth } from "../../../context/AuthContext";
import { AiFieldHeader } from "../../../components/shared/AiFieldHeader";
import { analyzeUploadedFile } from "../../../services/aiService";
import { uploadTempFile } from "../../../services/storageUpload";
import { useToast } from "../../../components/Toast";
import { generateDocCode } from "../../../utils/idGenerator";
import { getDeptCodeByEmail } from "../../../config/roles";
import { getDeptNameByCode } from "../../../constants/departments";
import { ApprovalModule } from "../../../types/legalOps";

interface Props {
  initialData?: Partial<SignRequest>;
  sourceInfo: {
    sourceType: SignSourceType | 'sealRequest';
    sourceId: string;
    sourceTitle: string;
    category: string;
  };
  onClose: () => void;
  onSuccess: () => void;
}

export function SignRequestFormModal({ initialData, sourceInfo, onClose, onSuccess }: Props) {
  const { createDraftFromSource, submitToLegal, loading: wfLoading } = useSignRequestWorkflow();
  const { user } = useAuth();
  const { showToast } = useToast();
  
  const [form, setForm] = useState({
    title: initialData?.title || sourceInfo.sourceTitle,
    description: initialData?.description || "",
    category: initialData?.category || sourceInfo.category,
    legalBasis: initialData?.legalBasis || "",
    amount: initialData?.amount || 0,
    consultedDepts: initialData?.consultedDepts || "",
    otherDeptComments: initialData?.otherDeptComments || "",
    attachments: initialData?.attachments || [],
    docCode: (initialData as any)?.docCode || "",
    requesterName: (initialData as any)?.requesterName || user?.displayName || ""
  });
  
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  // Auto-Gen DocCode if missing (Use SEAL-YYYYMMDD-XXX format)
  useEffect(() => {
      if (!initialData?.id && !form.docCode) {
          // We generate a temp code for display, backend or actual save will finalize it if needed.
          // Using ID generator utility to ensure consistency
          const code = generateDocCode(sourceInfo.sourceType === 'sealRequest' ? 'seal' : 'sign', new Date(), []);
          setForm(p => ({...p, docCode: code }));
      }
  }, []);

  // AI Helper
  const handleAiClick = async () => {
      const hasPending = pendingFiles.length > 0;
      const hasExisting = form.attachments.length > 0;

      if (!hasPending && !hasExisting) {
          showToast("請先上傳文件", "error");
          return;
      }

      setAiLoading(true);
      try {
          let targetMeta: AttachmentMeta | undefined = undefined;
          
          if (hasPending) {
              targetMeta = await uploadTempFile(pendingFiles[0]);
          } else {
              targetMeta = form.attachments[0];
          }

          if (!targetMeta) throw new Error("File upload failed");

          const result = await analyzeUploadedFile({
              fileMeta: targetMeta,
              purpose: "seal_request",
              extraContext: { category: form.category }
          });

          setForm(prev => ({
              ...prev,
              title: prev.title || result.title || "",
              description: result.description || result.summary || prev.description,
          }));
          showToast("AI 解析完成");

      } catch(e: any) {
          console.error(e);
          showToast("AI 解析失敗: " + e.message, "error");
      } finally {
          setAiLoading(false);
      }
  };

  const handleSave = async (submitNow: boolean) => {
    if (!form.title) { alert("請填寫標題"); return; }
    
    setIsSubmitting(true);
    try {
        let reqId = initialData?.id;
        
        // --- BRANCH A: Seal Request (Unified ApprovalRequests) ---
        if (sourceInfo.sourceType === 'sealRequest') {
             const userDeptCode = getDeptCodeByEmail(user?.email);
             const deptName = getDeptNameByCode(userDeptCode); // Map to name

             const payload: any = {
                 module: 'sealRequest' as ApprovalModule,
                 title: form.title,
                 description: form.description,
                 docCode: form.docCode,
                 docCategory: form.category,
                 department: deptName,
                 requesterName: form.requesterName,
                 requesterUid: user?.id || '',
                 priority: 'normal',
                 sensitivity: 'normal',
                 status: submitNow ? 'IN_REVIEW' : 'DRAFT',
                 // Extra fields
                 consultedDepts: form.consultedDepts,
                 otherDeptComments: form.otherDeptComments,
                 attachments: form.attachments // existing
             };

             if (!reqId) {
                 reqId = await createApprovalRequest(payload);
             } else {
                 await updateApprovalRequest(reqId, payload);
             }

             // Handle Pending Files
             if (pendingFiles.length > 0 && reqId) {
                 const newAtts = [];
                 for(const f of pendingFiles) {
                     const m = await uploadApprovalFile(reqId, 'request', f, user?.email||'');
                     newAtts.push(m);
                 }
                 // Combine existing + new
                 await updateApprovalRequest(reqId, { attachments: [...form.attachments, ...newAtts] });
             }

             // Start Flow if Submitting
             if (submitNow && reqId) {
                 await createOrResetApprovalFlow(
                     "SEAL_REQUEST",
                     reqId,
                     user?.id || "",
                     deptName,
                     form.requesterName
                 );
                 showToast("用印申請已送出 (進入簽核)");
             } else {
                 showToast("用印申請已暫存");
             }

        // --- BRANCH B: Generic Sign Request (Legacy Collection) ---
        } else {
            if (!reqId) {
                reqId = await createDraftFromSource(sourceInfo as any, form as any);
            }
            
            // Upload Logic for Legacy
            // Note: createDraftFromSource in workflow might handle creation differently.
            // Here we just handle file append if needed.
            // For simplicity, we assume createDraftFromSource works, but let's handle files manually if needed.
            // Actually, `createDraftFromSource` doesn't handle files upload inside itself well for pendingFiles.
            
            // Let's rely on the workflow hooks if possible, but we are inside the modal.
            // To keep it simple: If legacy, we assume `createDraftFromSource` created the doc.
            
            // NOTE: This branch is for "General Sign Request" not specifically "Seal". 
            // If `sourceType` is NOT `sealRequest`, it goes here.
            
            if (submitNow && reqId) {
                 const { getSignRequest } = await import("../services/signRequestService");
                 const fresh = await getSignRequest(reqId);
                 if (fresh) await submitToLegal(fresh);
            }
            showToast("簽呈已儲存");
        }
        
        onSuccess();
        onClose();
    } catch(e) {
        console.error(e);
        alert("儲存失敗");
    } finally {
        setIsSubmitting(false);
    }
  };

  return (
    <Modal title={sourceInfo.sourceType==='sealRequest' ? "新增用印申請" : "建立／編輯簽呈"} onClose={onClose} width={700}>
      <div className="grid">
         <div className="field" style={{gridColumn:"1/-1"}}>
            <label className="label">單號 (自動產生)</label>
            <input className="input" value={form.docCode} readOnly style={{background:'rgba(255,255,255,0.05)', color:'#fbbf24', fontFamily:'monospace'}} />
         </div>
         <div className="field" style={{gridColumn:"1/-1"}}>
            <label className="label">文件標題 <span style={{color:'red'}}>*</span></label>
            <input className="input" value={form.title} onChange={e=>setForm({...form, title:e.target.value})} placeholder="例如：113年度經銷合約用印" />
         </div>
         <div className="field">
            <label className="label">申請人</label>
            <input className="input" value={form.requesterName} onChange={e=>setForm({...form, requesterName:e.target.value})} />
         </div>
         <div className="field">
            <label className="label">類別</label>
            <input className="input" value={form.category} onChange={e=>setForm({...form, category:e.target.value})} />
         </div>
         
         {/* Extended Fields for Seal/Sign */}
         <div className="field" style={{gridColumn:"1/-1"}}>
             <label className="label">已徵詢部門 (選填)</label>
             <input className="input" value={form.consultedDepts} onChange={e=>setForm({...form, consultedDepts:e.target.value})} placeholder="例如：財務部、採購部" />
         </div>

         <div className="field" style={{gridColumn:"1/-1"}}>
            <AiFieldHeader 
                label="說明 / 擬辦 (AI 支援)"
                loading={aiLoading}
                onClick={handleAiClick}
                disabled={pendingFiles.length === 0 && form.attachments.length === 0}
            />
            <textarea 
                className="input" 
                rows={4} 
                value={form.description} 
                onChange={e=>setForm({...form, description:e.target.value})} 
                placeholder="請說明用印目的、文件內容摘要..."
            />
         </div>
      </div>
      
      <FileUploadKit 
        label="上傳用印文件 / 附件"
        existingFiles={form.attachments} 
        rawFiles={pendingFiles}
        onFilesSelected={fs => setPendingFiles(p => [...p, ...fs])} 
        onRemoveRawFile={(idx) => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}
      />
      
      <div className="row" style={{marginTop:20, justifyContent:'flex-end', gap: 10}}>
         <button className="btn" style={{width:'auto', background:'transparent', border:'1px solid #555', color:'#ccc'}} onClick={() => handleSave(false)} disabled={isSubmitting}>
             暫存 (Draft)
         </button>
         <button className="btn" style={{width:'auto'}} onClick={() => handleSave(true)} disabled={isSubmitting}>
             存檔並送出呈核
         </button>
      </div>
    </Modal>
  );
}
